import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Swal from "sweetalert2";
import AttendanceServices from "../../../services/AttendanceServices";
import VehicleServics from "../../../services/VehicleServics";

const CheckIn = ({ show, handleClose, onSuccess }) => {
  const [startKM, setStartKM] = useState("");
  const [meterPhoto, setMeterPhoto] = useState(null);
  const [error, setError] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [previewImage, setPreviewImage] = useState("");
  const [isCompressing, setIsCompressing] = useState(false);
  const [dieselInReading, setDieselInReading] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  const user = JSON.parse(localStorage.getItem("userDetails"));
  const availableStores = user?.data?.user?.store || [];

  // ✅ FIX: only auto select if ONE store
  const defaultStore =
    availableStores.length === 1 ? availableStores[0]._id : "";

  const [selectedStore, setSelectedStore] = useState(defaultStore);

  const handleCloseWithRefresh = () => {
    handleClose();
    onSuccess(); // 🔥 refresh parent table
  };


  useEffect(() => {
    if (!show) return;

    setVehicles([]);
    setSelectedVehicle("");

    const storeId = selectedStore || availableStores[0]?._id;

    if (storeId) {
      setLoadingVehicles(true);

      VehicleServics.getStoreVehicle(storeId)
        .then((vehicleRes) => {
          const allVehicles = vehicleRes?.data || [];
          setVehicles(allVehicles);
        })
        .catch((error) => {
          console.log(
            error?.response?.data?.message ||
            "Something went wrong!"
          );
        })
        .finally(() => {
          setLoadingVehicles(false);
        });
    }
  }, [selectedStore, show]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // ✅ TYPE VALIDATION (NEW)
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      Swal.fire("Error", "Only JPG/PNG images allowed", "error");
      return;
    }

    // ✅ FILE SIZE LIMIT (same as yours)
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("Error", "Image must be less than 5MB", "error");
      return;
    }

    // ✅ CLEAN OLD PREVIEW (NEW - memory fix)
    if (previewImage) {
      URL.revokeObjectURL(previewImage);
    }

    // ✅ Preview immediately (same UI)
    const previewURL = URL.createObjectURL(file);
    setPreviewImage(previewURL);

    // ✅ SKIP COMPRESSION FOR SMALL FILES (NEW - performance)
    if (file.size < 300 * 1024) {
      setMeterPhoto(file);
      return;
    }

    setIsCompressing(true);

    try {
      const img = new Image();
      img.src = previewURL;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_WIDTH = 800;

        const scaleSize = MAX_WIDTH / img.width;
        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scaleSize;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            setIsCompressing(false);

            if (!blob) {
              setMeterPhoto(file);
              return;
            }

            const compressedFile = new File([blob], file.name, {
              type: "image/jpeg",
            });

            setMeterPhoto(compressedFile);
          },
          "image/jpeg",
          0.7 // 🔥 SAME (no UI change)
        );
      };

      img.onerror = () => {
        setIsCompressing(false);
        setMeterPhoto(file);
      };
    } catch (err) {
      setIsCompressing(false);
      setMeterPhoto(file);
    }
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();
    setError("");

    // 🔥 FINAL SAFETY CHECK
    if (availableStores.length > 1 && !selectedStore) {
      Swal.fire({
        icon: "warning",
        title: "Select Store First",
        text: "कृपया पहले स्टोर चुनें।",
      });
      return;
    }

    if (!startKM) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "प्रारंभिक HMR आवश्यक है।",
      });
      return;
    }

    if (!meterPhoto) {
      Swal.fire({
        icon: "warning",
        title: "Image Required",
        text: "कृपया मीटर की फोटो अपलोड करें",
      });
      return;
    }

    if (!selectedVehicle) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "कृपया वाहन चुनें।",
      });
      return;
    }


    if (!dieselInReading) {
      Swal.fire({
        icon: "warning",
        title: "Diesel Required",
        text: "प्रारंभिक डीज़ल रीडिंग आवश्यक है",
      });
      return;
    }

    const formData = new FormData();

    // ✅ Required
    formData.append("startKM", startKM);
    formData.append("vehicleId", selectedVehicle);

    // ✅ Image (compressed)
    formData.append("image", meterPhoto);

    // ✅ Diesel (NEW)
    formData.append("dieselInReading", dieselInReading);

    try {
      await AttendanceServices.checkIn(formData, (progressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setUploadProgress(percent);
      });

      setStartKM("");
      setDieselInReading("");
      setMeterPhoto(null);
      setPreviewImage("");

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "चेकइन सफल रहा।",
      });

     handleCloseWithRefresh(); // ✅ FIXED
    }

    catch (error) {

      // 🔥 CASE: VEHICLE ALREADY RUNNING
      if (error?.response?.data?.requireForce) {

        Swal.fire({
          title: "Vehicle Already Running",
          text: "यह वाहन पहले से किसी और ऑपरेटर द्वारा चालू है। क्या आप जारी रखना चाहते हैं?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, Continue",
          cancelButtonText: "Cancel",
        }).then(async (result) => {

          if (result.isConfirmed) {

            try {
              const formDataForce = new FormData();

              formDataForce.append("startKM", startKM);
              formDataForce.append("vehicleId", selectedVehicle);
              formDataForce.append("image", meterPhoto);
              formDataForce.append("dieselInReading", dieselInReading);
              formDataForce.append("force", true); // 🔥 IMPORTANT

              await AttendanceServices.checkIn(formDataForce);

              Swal.fire({
                icon: "success",
                title: "Success!",
                text: "Force Check-in successful",
              });

              // 🔥 RESET FORM
              setStartKM("");
              setDieselInReading("");
              setMeterPhoto(null);
              setPreviewImage("");

             handleCloseWithRefresh(); // ✅ FIXED

            } catch (err2) {
              Swal.fire(
                "Error",
                err2?.response?.data?.message || "Force check-in failed",
                "error"
              );
            }

          }
        });

      } else {
        // 🔥 NORMAL ERROR
        Swal.fire({
          icon: "error",
          title: "Error!",
          text:
            error.response?.data?.message ||
            "There is some problem in data fetching!",
        });
      }
    }
  };

  return (
    <Modal show={show} onHide={handleCloseWithRefresh} centered>
      <Modal.Header closeButton>
        <Modal.Title>Check In</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleCheckIn}>

          {/* VEHICLE */}
          <div className="form-group mb-3">
            <label className="fw-bold">Select Vehicle</label>

            {loadingVehicles ? (
              <div className="text-center py-3">
                <div className="spinner-border text-primary"></div>
                <div className="small text-muted mt-2">
                  Loading vehicles...
                </div>
              </div>
            ) : (
              <select
                className="form-control"
                value={selectedVehicle}
                onChange={(e) => setSelectedVehicle(e.target.value)}
                disabled={loadingVehicles}
              >
                <option value="">-- वाहन चुनें --</option>

                {vehicles.map((vehicle) => (
                  <option key={vehicle._id} value={vehicle._id}>
                    {vehicle.name} - {vehicle.equipmentNo} - {vehicle.model}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* HMR */}
          <div className="form-group mb-3">
            <label className="fw-bold">Starting HMR</label>
            <input
              type="number"
              className="form-control"
              placeholder="प्रारंभिक HMR दर्ज करें"
              value={startKM}
              onChange={(e) => setStartKM(e.target.value)}
            />
          </div>

          {/* IMAGE */}
          <div className="form-group mb-3">
            <label className="fw-bold mb-1">मीटर फोटो</label>

            <input
              type="file"
              className="form-control"
              accept="image/*,camera/*"
              capture="environment"
              onClick={(e) => (e.target.value = null)}
              onChange={handleFileChange}
            />

            <small className="text-muted d-block mt-1">
              Camera या gallery दोनों से फोटो चुन सकते हैं
            </small>

            {/* LOADER (Compression) */}
            {isCompressing && (
              <div className="text-center mt-3">
                <div className="spinner-border text-primary" role="status"></div>
                <div className="small text-muted mt-1">
                  Image optimize हो रही है...
                </div>
              </div>
            )}

            {/* PREVIEW */}
            {previewImage && !isCompressing && (
              <div
                className="mt-3"
                style={{
                  border: "1px solid #e9ecef",
                  borderRadius: "10px",
                  padding: "12px",
                  background: "#f8f9fa",
                  textAlign: "center",
                }}
              >
                <img
                  src={previewImage}
                  alt="Preview"
                  style={{
                    maxHeight: "180px",
                    width: "100%",
                    objectFit: "contain",
                    borderRadius: "6px",
                  }}
                />

                {/* File info (optional but useful) */}
                <div className="small text-muted mt-2">
                  Preview Ready
                </div>
              </div>
            )}

            {/* UPLOAD PROGRESS */}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-3">
                <div className="progress" style={{ height: "8px" }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>

                <div className="text-end small text-muted mt-1">
                  Uploading... {uploadProgress}%
                </div>
              </div>
            )}
          </div>

          {/* DIESEL */}
          <div className="form-group mb-3">
            <label className="fw-bold">Diesel Reading</label>
            <input
              type="number"
              className="form-control"
              placeholder="प्रारंभिक डीज़ल रीडिंग दर्ज करें"
              value={dieselInReading}
              onChange={(e) => setDieselInReading(e.target.value)}
            />
          </div>

          {/* BUTTONS */}
          <Modal.Footer className="px-0">
            <Button variant="secondary" onClick={handleCloseWithRefresh}>
              Close
            </Button>

            <Button
              variant="primary"
              type="submit"
              disabled={isCompressing || uploadProgress > 0}
            >
              {isCompressing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Processing...
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </Modal.Footer>

        </form>
      </Modal.Body>
    </Modal>
  );
};

export default CheckIn;